import { useAuthContext } from '@/auth/useAuthContext';
import { useConfirm } from '@/components/Confirm';
/**
 * /settings/api-keys — issue + revoke API keys for the /api/v1/* surface.
 *
 * Enterprise-tier only. The full key plaintext is shown ONCE on creation in
 * a copyable code block. Non-enterprise tenants see an Upgrade CTA.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface KeyRow {
  id: string;
  name: string;
  prefix: string;
  suffix: string;
  scopes: string[];
  createdByMembershipId: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
}

interface OneTimeKey {
  id: string;
  name: string;
  plaintext: string;
}

const ALL_SCOPES = [
  'bookings:read',
  'bookings:write',
  'customers:read',
  'customers:write',
  'pets:read',
  'pets:write',
  'payments:read',
  'services:read',
  'webhooks:manage',
  '*',
] as const;

export function ApiKeysSettings() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount ?? null;
  const isEnterprise = account?.tier === 'enterprise';

  const [rows, setRows] = useState<KeyRow[]>([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['bookings:read']);
  const [oneTime, setOneTime] = useState<OneTimeKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/api-keys', { credentials: 'include' });
      if (res.ok) {
        const body = (await res.json()) as { keys: KeyRow[] };
        setRows(body.keys);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleScope = (s: string) => {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const create = useCallback(async () => {
    if (!name.trim() || scopes.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), scopes }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; issues?: string[] };
        if (err?.error === 'tier_locked') {
          setError('API keys require the Enterprise plan.');
        } else {
          setError(err?.issues?.join(', ') ?? err?.error ?? 'Create failed.');
        }
        return;
      }
      const body = (await res.json()) as {
        key: { id: string; name: string };
        plaintext: string;
      };
      setOneTime({ id: body.key.id, name: body.key.name, plaintext: body.plaintext });
      setName('');
      setScopes(['bookings:read']);
      await load();
    } finally {
      setSaving(false);
    }
  }, [name, scopes, load]);

  const revoke = useCallback(
    async (id: string) => {
      const yes = await confirm({
        title: 'Revoke this API key?',
        message:
          'Revoking takes effect immediately. Any integration using this key will start receiving 401 Unauthorized.',
        confirmLabel: 'Revoke key',
        danger: true,
      });
      if (!yes) return;
      const res = await fetch(`/api/api-keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) await load();
    },
    [load, confirm],
  );

  const copyKey = async () => {
    if (!oneTime) return;
    await navigator.clipboard.writeText(oneTime.plaintext).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="py-4 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <ConfirmDialog />

      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">API keys</h1>
        <p className="text-sm text-muted-foreground">
          Issue API keys for the public REST API at <code>/api/v1/*</code>. See the{' '}
          <a className="underline" href="/docs" target="_blank" rel="noreferrer">
            developer documentation
          </a>{' '}
          for endpoints, schemas, and SDK examples.
        </p>
      </header>

      {!isEnterprise ? (
        <div className="rounded-md bg-warning/10 border border-warning px-4 py-3">
          <p className="font-medium text-sm">API access requires the Enterprise plan</p>
          <p className="text-sm text-muted-foreground mt-1">
            Upgrade to issue API keys, set up outbound webhooks, and integrate with your own tools.
          </p>
          <Link
            to="/settings/billing"
            className="inline-block mt-2 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            Upgrade to Enterprise
          </Link>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md bg-warning/10 border border-warning px-3 py-2 text-sm text-warning">
          {error}
        </div>
      ) : null}

      {oneTime ? (
        <div className="rounded-md bg-success/10 border border-success px-3 py-2 text-sm">
          <p className="font-medium">Key created: {oneTime.name}</p>
          <pre className="num mt-2 text-xs overflow-x-auto rounded bg-card border border-border p-2">
            {oneTime.plaintext}
          </pre>
          <p className="text-xs text-muted-foreground mt-1">
            Save this now — you won't see it again. Use it as
            <code className="ml-1">Authorization: Bearer {'<key>'}</code>.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="text-xs rounded-md bg-primary text-primary-foreground px-2 py-1"
              onClick={() => void copyKey()}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className="text-xs underline" onClick={() => setOneTime(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {isEnterprise ? (
        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="text-sm font-medium">Create new key</h2>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor="api-key-name">
              Name
            </label>
            <input
              id="api-key-name"
              className="block w-full text-sm rounded-md border border-border px-3 py-2"
              type="text"
              placeholder="e.g. Zapier integration"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Scopes</p>
            <div className="flex flex-wrap gap-2">
              {ALL_SCOPES.map((s) => (
                <label
                  key={s}
                  className={`text-xs rounded-md border px-2 py-1 cursor-pointer ${
                    scopes.includes(s)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={scopes.includes(s)}
                    onChange={() => toggleScope(s)}
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            onClick={() => void create()}
            disabled={saving || !name.trim() || scopes.length === 0}
          >
            Create key
          </button>
        </section>
      ) : null}

      <section>
        <h2 className="text-sm font-medium mb-2">Active keys</h2>
        <ul className="space-y-2">
          {rows.length === 0 ? (
            <li className="text-sm text-muted-foreground">No keys issued yet.</li>
          ) : null}
          {rows.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-border bg-card p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
            >
              <div className="md:col-span-4">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="num text-xs text-muted-foreground">
                  {r.prefix}…{r.suffix}
                </div>
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-1">
                {r.scopes.map((s) => (
                  <span key={s} className="text-xs rounded-md bg-muted px-1.5 py-0.5 font-mono">
                    {s}
                  </span>
                ))}
              </div>
              <div className="md:col-span-3 text-xs text-muted-foreground">
                {r.revokedAt ? (
                  <span className="text-warning">Revoked</span>
                ) : (
                  <>Last used: {r.lastUsedAt ?? 'never'}</>
                )}
              </div>
              <div className="md:col-span-1 flex justify-end">
                {!r.revokedAt ? (
                  <button
                    type="button"
                    className="text-xs text-warning underline"
                    onClick={() => void revoke(r.id)}
                  >
                    Revoke
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
